import { motion } from 'framer-motion'
import { Game2048State } from '../../../types/game.types'

interface Game2048BoardProps {
  state: Game2048State
  onMove: (direction: 'up' | 'down' | 'left' | 'right') => void
}

const TILE_COLORS: Record<number, string> = {
  0: 'bg-surface/50',
  2: 'bg-amber-50 text-amber-900',
  4: 'bg-amber-100 text-amber-900',
  8: 'bg-orange-200 text-orange-900',
  16: 'bg-orange-300 text-orange-900',
  32: 'bg-orange-400 text-orange-900',
  64: 'bg-orange-500 text-white',
  128: 'bg-yellow-400 text-yellow-900',
  256: 'bg-yellow-500 text-white',
  512: 'bg-yellow-600 text-white',
  1024: 'bg-amber-600 text-white',
  2048: 'bg-amber-700 text-white',
  4096: 'bg-red-600 text-white',
  8192: 'bg-red-700 text-white'
}

const TILE_SIZES: Record<number, string> = {
  0: 'text-0',
  2: 'text-2xl',
  4: 'text-2xl',
  8: 'text-2xl',
  16: 'text-2xl',
  32: 'text-2xl',
  64: 'text-2xl',
  128: 'text-xl',
  256: 'text-xl',
  512: 'text-xl',
  1024: 'text-lg',
  2048: 'text-lg',
  4096: 'text-lg',
  8192: 'text-lg'
}

export default function Game2048Board({ state, onMove }: Game2048BoardProps) {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (state.gameOver) return
    
    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault()
        onMove('up')
        break
      case 'ArrowDown':
        e.preventDefault()
        onMove('down')
        break
      case 'ArrowLeft':
        e.preventDefault()
        onMove('left')
        break
      case 'ArrowRight':
        e.preventDefault()
        onMove('right')
        break
    }
  }
  
  return (
    <div 
      className="focus:outline-none" 
      tabIndex={0}
      onKeyDown={handleKeyDown}
      aria-label="2048 game board. Use arrow keys to move tiles."
    >
      <div className="relative bg-surface rounded-2xl p-4 shadow-xl">
        <div className="grid grid-cols-4 gap-3">
          {state.grid.map((row, rowIndex) => (
            row.map((value, colIndex) => {
              const colorClass = TILE_COLORS[value] || 'bg-red-800 text-white'
              const sizeClass = TILE_SIZES[value] || 'text-base'
              const isNew = state.moves === 0 && value > 0 // Simple new tile detection
              
              return (
                <motion.div
                  key={`${rowIndex}-${colIndex}`}
                  className={`
                    w-20 h-20 rounded-lg flex items-center justify-center
                    font-bold ${colorClass} ${sizeClass}
                    ${value === 0 ? 'border border-border/30' : 'shadow-md'}
                  `}
                  initial={isNew ? { scale: 0 } : false}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                  whileHover={{ scale: value > 0 ? 1.05 : 1 }}
                  aria-label={`Tile at position ${rowIndex + 1}, ${colIndex + 1}. ${value > 0 ? `Value: ${value}` : 'Empty'}`}
                >
                  {value > 0 && (
                    <motion.span
                      key={value}
                      initial={{ scale: 0.8, opacity: 0.5 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ duration: 0.2 }}
                    >
                      {value}
                    </motion.span>
                  )}
                </motion.div>
              )
            })
          ))}
        </div>
        
        {/* Grid lines */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-0 right-0 h-0.5 bg-border/30 -translate-y-1/2" />
          <div className="absolute top-2/4 left-0 right-0 h-0.5 bg-border/30 -translate-y-1/2" />
          <div className="absolute top-3/4 left-0 right-0 h-0.5 bg-border/30 -translate-y-1/2" />
          <div className="absolute left-1/4 top-0 bottom-0 w-0.5 bg-border/30 -translate-x-1/2" />
          <div className="absolute left-2/4 top-0 bottom-0 w-0.5 bg-border/30 -translate-x-1/2" />
          <div className="absolute left-3/4 top-0 bottom-0 w-0.5 bg-border/30 -translate-x-1/2" />
        </div>
        
        {/* Game over overlay */}
        {state.gameOver && (
          <motion.div
            className="absolute inset-0 bg-background/90 rounded-2xl flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div className="text-center p-8">
              <h3 className="text-2xl font-bold text-text mb-2">
                {state.won ? 'You Win! 🎉' : 'Game Over! 😢'}
              </h3>
              <p className="text-text-muted">
                {state.won 
                  ? 'You reached 2048! Keep playing to get a higher score.' 
                  : 'No more moves available.'
                }
              </p>
            </div>
          </motion.div>
        )}
      </div>
      
      {/* Controls hint */}
      <div className="mt-6 text-center text-text-muted text-sm">
        <p>Use <kbd className="px-2 py-1 bg-surface rounded border border-border">Arrow Keys</kbd> or swipe to move tiles</p>
      </div>
    </div>
  )
}